import moment from "moment";

// Shared career/job helpers. Kept out of the page components because the
// listing, the detail page and the apply form all need the same idea of
// "is this opening still open?" — and a card that says Open must never link
// through to a detail page that says Closed.

export const CLOSING_FORMAT = "DD MMM YYYY";

// Openings are "new" for their first few days on the site.
const NEW_FOR_DAYS = 3;

export const parseClosingDate = (closingDate) => {
  if (!closingDate) return null;
  const date = moment(closingDate, CLOSING_FORMAT);
  return date.isValid() ? date : null;
};

const isJobNew = (createDate) => {
  if (!createDate) return false;
  const created = moment(createDate);
  if (!created.isValid()) return false;
  const daysOld = moment().diff(created, "days");
  return daysOld >= 0 && daysOld <= NEW_FOR_DAYS;
};

// Missing or unparseable closing date counts as closed — better to hide an
// apply button that should be live than to collect applications for a post
// that has already been filled.
const isJobClosed = (closingDate) => {
  const date = parseClosingDate(closingDate);
  return !date || date.isBefore(moment().startOf("day"));
};

// Amber once the deadline is within a week, so a soon-to-close post reads
// as urgent rather than just "open".
const getStatusColor = (closingDate, closed) => {
  if (closed) return "neutral";
  const date = parseClosingDate(closingDate);
  if (!date) return "neutral";
  return date.diff(moment(), "days") <= 7 ? "accent" : "secondary";
};

export const countVacancies = (departments = []) =>
  departments.reduce(
    (total, dept) => total + (dept.POSITIONS || []).reduce((sum, p) => sum + (Number(p.COUNT) || 0), 0),
    0
  );

// Fallback slug for records that predate the SLUG field, so an opening
// without one still gets a usable (if uglier) detail URL instead of vanishing
// from the listing.
const fallbackSlug = (job, index) =>
  (job.CAREER_ID || job.JOB_TITLE || `opening-${index}`)
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

// Decorates a raw career record with everything the UI derives from it.
// Applied once in the API provider so listing/detail/apply can never disagree.
export const withCareerMeta = (job, index = 0) => {
  const closed = isJobClosed(job.CLOSING);
  const slug = job.SLUG || fallbackSlug(job, index);
  return {
    ...job,
    SLUG: slug,
    id: job.CAREER_ID || slug,
    isNew: !closed && isJobNew(job.CREATE_DATE),
    isClosed: closed,
    statusColor: getStatusColor(job.CLOSING, closed),
    vacancyCount: countVacancies(job.DEPARTMENTS),
    departmentCount: (job.DEPARTMENTS || []).length,
  };
};
